import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { UserService } from 'src/app/core/common-services/user.services';

/** KYC status codes that BaseApiService handles with a silent redirect / dialog */
const SILENT_KYC_STATUS_CODES = [9001, 9002, 9003, 9004, 9005];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(
    private notificationService: NotificationService,
    private userService: UserService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldNotify(request, error)) {
          this.notify(error);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Skip the flows that are already handled elsewhere so the user
   * does not get two toasters for the same failure.
   */
  private shouldNotify(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    // Translation / static assets
    if (request.url.includes('i18n') || request.url.includes('assets/')) {
      return false;
    }

    // Refresh token & logout flows end with a redirect to login
    if (request.url.includes('RefreshToken') || request.url.includes('Logout')) {
      return false;
    }

    // For a logged in user these are handled by BaseApiService (token refresh / logout).
    // When there is no session (login, registration, ...) the message is still relevant.
    if ((error.status === 0 || error.status === 401) && this.userService.isLoggedIn()) {
      return false;
    }

    // KYC status codes are handled by BaseApiService with a redirect or a dialog
    if (SILENT_KYC_STATUS_CODES.includes(error?.error?.statusCode)) {
      return false;
    }

    return true;
  }

  private notify(error: HttpErrorResponse): void {
    if (error.status === 0) {
      this.showError('Shared.NO_INTERNET_CONNECTION');
      return;
    }

    if (error.error instanceof Blob) {
      error.error.text().then(text => {
        let message: string | undefined;
        try {
          const body = JSON.parse(text);
          message = body?.message ?? body?.data?.message;
        } catch {
          message = undefined;
        }
        this.showError(message);
      });
      return;
    }

    let msg = error?.error?.message ?? error?.error?.data?.message ?? error?.error?.title;
    if (!msg && typeof error?.error === 'string') {
      msg = error.error;
    }
    this.showError(msg);
  }

  private showError(message?: string): void {
    this.notificationService.error(message || 'Shared.ERROR_OCCURRED');
  }
}
